import { z } from 'zod';
import { IsoTimestamp, UserId } from './common';

/**
 * EmailAuthGrant — a credential the user has given OutLinked so it can read
 * ATS verification-code emails on their behalf. Two flavors:
 *
 *  - OAuth providers (Google / Microsoft) — the gold standard: scoped
 *    read-only access, revocable from the provider's account screen,
 *    refresh-token rotation. Credentials live in Secrets Manager keyed by
 *    userId+provider.
 *
 *  - IMAP — everything else (Yahoo, iCloud, Fastmail, Zoho, Proton via
 *    Bridge, ISP mailboxes, generic). Requires an app-specific password
 *    from the user; we store host/port/tls + username + password in
 *    Secrets Manager. Higher-trust grant than OAuth — disclose clearly.
 *
 * Code-extraction happens cloud-side via /v1/email/verification-code; the
 * worker never sees mail bytes.
 */

export const EmailAuthType = z.enum(['oauth', 'imap']);
export type EmailAuthType = z.infer<typeof EmailAuthType>;

const OAUTH_EMAIL_PROVIDER_VALUES = ['google', 'microsoft'] as const;
export const OAuthEmailProvider = z.enum(OAUTH_EMAIL_PROVIDER_VALUES);
export type OAuthEmailProvider = z.infer<typeof OAuthEmailProvider>;

/** Forward-compat read variant — see #482. */
export const OAuthEmailProviderReadable = z
  .enum([...OAUTH_EMAIL_PROVIDER_VALUES, 'unknown'] as const)
  .catch('unknown');
export type OAuthEmailProviderReadable = z.infer<typeof OAuthEmailProviderReadable>;

/**
 * Discriminated union: OAuth or IMAP. `provider` is shorthand and used as
 * the entity key on DynamoDB; for IMAP it's literally 'imap'.
 */
const OAuthGrant = z.object({
  authType: z.literal('oauth'),
  userId: UserId,
  provider: OAuthEmailProviderReadable,
  email: z.string().email(),
  scopes: z.array(z.string()).default([]),
  grantedAt: IsoTimestamp,
  lastUsedAt: IsoTimestamp.optional(),
  revokedAt: IsoTimestamp.optional(),
});

const ImapGrant = z.object({
  authType: z.literal('imap'),
  userId: UserId,
  provider: z.literal('imap'),
  email: z.string().email(),
  /** IMAP host, e.g., imap.gmail.com or imap.fastmail.com */
  imapHost: z.string().min(1),
  imapPort: z.number().int().positive().max(65535).default(993),
  imapTls: z.boolean().default(true),
  /** Friendly label for the user, e.g., "Personal Fastmail" */
  label: z.string().optional(),
  grantedAt: IsoTimestamp,
  lastUsedAt: IsoTimestamp.optional(),
  revokedAt: IsoTimestamp.optional(),
});

export const EmailAuthGrant = z.discriminatedUnion('authType', [OAuthGrant, ImapGrant]);
export type EmailAuthGrant = z.infer<typeof EmailAuthGrant>;
export type OAuthEmailAuthGrant = z.infer<typeof OAuthGrant>;
export type ImapEmailAuthGrant = z.infer<typeof ImapGrant>;

/** OAuth flow — `provider` is in the route path. */
export const CreateOAuthGrantRequest = z.object({
  /** OAuth authorization code returned from the provider's consent screen. */
  code: z.string().min(1),
  /** Redirect URI used during the consent flow (must match exactly). */
  redirectUri: z.string().url(),
  /**
   * PKCE verifier the mobile client generated when starting the consent
   * dance. The backend submits this with the code-for-token exchange in
   * place of a Web-client `client_secret` — iOS OAuth clients don't issue
   * secrets, so PKCE is what proves the exchange is happening from the
   * legitimate client. Optional so the existing Web-client flow stays
   * back-compatible; the handler picks one or the other based on which
   * is provided.
   */
  codeVerifier: z.string().min(1).optional(),
});
export type CreateOAuthGrantRequest = z.infer<typeof CreateOAuthGrantRequest>;

/** IMAP flow — user enters host + credentials in mobile/desktop UI. */
export const CreateImapGrantRequest = z.object({
  email: z.string().email(),
  imapHost: z.string().min(1),
  imapPort: z.number().int().positive().max(65535).default(993),
  imapTls: z.boolean().default(true),
  /** App-specific password the user generated in their mail provider. */
  password: z.string().min(1),
  label: z.string().optional(),
});
export type CreateImapGrantRequest = z.infer<typeof CreateImapGrantRequest>;

/** Worker → Cloud — find a code matching senderDomain in the last windowSec seconds. */
export const FetchVerificationCodeRequest = z.object({
  senderDomain: z.string().min(3),
  windowSec: z.number().int().positive().max(900).default(300),
  /** Optional subject keywords to narrow the match (e.g., ["verification", "code"]) */
  subjectContains: z.array(z.string()).optional(),
});
export type FetchVerificationCodeRequest = z.infer<typeof FetchVerificationCodeRequest>;

export const FetchVerificationCodeResponse = z.object({
  found: z.boolean(),
  code: z.string().optional(),
  /**
   * Click-link variant. Workday and some other ATSes send a one-shot
   * URL instead of (or alongside) a 6-digit code. When present, the
   * worker navigates to it in the same Playwright context so the
   * activation happens with the same cookies / IP / browser identity
   * that created the account. `found` is true whenever EITHER `code`
   * or `verificationLink` is set.
   */
  verificationLink: z.string().url().optional(),
  matchedSenderDomain: z.string().optional(),
  messageId: z.string().optional(),
  subject: z.string().optional(),
  receivedAt: IsoTimestamp.optional(),
  /** Which provider/grant served the code (for the audit row). */
  servedByProvider: z.string().optional(),
  /** Audit-log id written to DynamoDB so the user can review. */
  auditId: z.string().optional(),
});
export type FetchVerificationCodeResponse = z.infer<typeof FetchVerificationCodeResponse>;

/** Common known IMAP server presets — used by the mobile/desktop UI to auto-fill the IMAP form. */
export const IMAP_PRESETS: Record<string, { host: string; port: number; tls: boolean }> = {
  'gmail.com': { host: 'imap.gmail.com', port: 993, tls: true },
  'icloud.com': { host: 'imap.mail.me.com', port: 993, tls: true },
  'me.com': { host: 'imap.mail.me.com', port: 993, tls: true },
  'mac.com': { host: 'imap.mail.me.com', port: 993, tls: true },
  'yahoo.com': { host: 'imap.mail.yahoo.com', port: 993, tls: true },
  'outlook.com': { host: 'outlook.office365.com', port: 993, tls: true },
  'hotmail.com': { host: 'outlook.office365.com', port: 993, tls: true },
  'live.com': { host: 'outlook.office365.com', port: 993, tls: true },
  'fastmail.com': { host: 'imap.fastmail.com', port: 993, tls: true },
  'zoho.com': { host: 'imap.zoho.com', port: 993, tls: true },
  'aol.com': { host: 'imap.aol.com', port: 993, tls: true },
  'protonmail.com': { host: '127.0.0.1', port: 1143, tls: false }, // local Bridge
};
